<template>
  <Layout>
    <div class="session-list-container">
      <div class="container-fluid">
        <div class="row">
          <div class="col-12">
            <h2 class="page-title">Browse Sessions</h2>
            
            <!-- 搜索和筛选区域 -->
            <div class="search-filter-section mb-4">
              <div class="row">
                <div class="col-md-3">
                  <div class="form-group">
                    <label for="searchKeyword">Search</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      id="searchKeyword" 
                      v-model="searchKeyword"
                      placeholder="Search sessions..."
                      @input="filterSessions"
                    >
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form-group">
                    <label for="primarySection">Primary Section</label>
                    <select class="form-control" id="primarySection" v-model="selectedPrimarySection" @change="filterSessions">
                      <option value="">All Sections</option>
                      <option value="21">Section 21</option>
                      <option value="99">Section 99</option>
                      <option value="SC">Section SC</option>
                      <option value="AS">Atmospheric Sciences (AS)</option>
                      <option value="OS">Ocean Sciences (OS)</option>
                      <option value="HS">Hydrological Sciences (HS)</option>
                      <option value="SE">Solid Earth Sciences (SE)</option>
                      <option value="ST">Solar and Terrestrial Sciences (ST)</option>
                      <option value="PS">Planetary Sciences (PS)</option>
                      <option value="BG">Biogeosciences (BG)</option>
                      <option value="IG">Interdisciplinary Geosciences (IG)</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form-group">
                    <label for="statusFilter">Status</label>
                    <select class="form-control" id="statusFilter" v-model="selectedStatus" @change="filterSessions">
                      <option value="">All Status</option>
                      <option value="Active">Active</option>
                      <option value="Withdrawn">Withdrawn</option>
                      <option value="Pending">Pending</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form-group">
                    <label for="sortBy">Sort By</label>
                    <select class="form-control" id="sortBy" v-model="sortBy" @change="sortSessions">
                      <option value="codeA">Session Code (A-Z)</option>
                      <option value="codeD">Session Code (Z-A)</option>
                      <option value="titleA">Title (A-Z)</option>
                      <option value="titleD">Title (Z-A)</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <!-- 结果统计 -->
            <div class="results-info mb-3">
              <span class="text-info">{{ filteredSessions.length }} record(s)</span>
            </div>

            <!-- Session列表表格 -->
            <div class="table-responsive">
              <table class="table table-striped table-hover">
                <thead class="thead-dark">
                  <tr>
                    <th>
                      <a href="#" @click.prevent="sortByField('code')" class="text-white">
                        Session
                        <i class="fa fa-sort" v-if="sortBy !== 'codeA' && sortBy !== 'codeD'"></i>
                        <i class="fa fa-sort-up" v-if="sortBy === 'codeA'"></i>
                        <i class="fa fa-sort-down" v-if="sortBy === 'codeD'"></i>
                      </a>
                    </th>
                    <th>Primary Section</th>
                    <th>Secondary Section</th>
                    <th>Conveners</th>
                    <th>
                      <a href="#" @click.prevent="sortByField('status')" class="text-white">
                        Status
                        <i class="fa fa-sort" v-if="sortBy !== 'statA' && sortBy !== 'statD'"></i>
                        <i class="fa fa-sort-up" v-if="sortBy === 'statA'"></i>
                        <i class="fa fa-sort-down" v-if="sortBy === 'statD'"></i>
                      </a>
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="session in paginatedSessions" :key="session.id" class="session-row">
                    <td>
                      <div class="session-info">
                        <a 
                          href="#" 
                          @click.prevent="viewSessionDetails(session.id)" 
                          class="session-code"
                        >
                          {{ session.session_no || 'N/A' }}
                        </a>
                        <div class="session-title">{{ session.title || 'No Title' }}</div>
                        <div class="session-meta">
                          <small class="text-muted">
                            Abstracts: {{ session.ab_num || 0 }} | 
                            Duration: {{ session.duration || 'N/A' }} | 
                            Room: {{ session.room || 'TBD' }}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span class="section-badge primary-section">{{ session.primary_section || 'N/A' }}</span>
                    </td>
                    <td>
                      <span 
                        v-if="session.secondary_section" 
                        class="section-badge secondary-section"
                      >
                        {{ session.secondary_section }}
                      </span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>
                      <div class="conveners-list" v-if="session.conveners">
                        <div class="convener-item">
                          {{ session.conveners }}
                        </div>
                      </div>
                      <div v-else class="text-muted">No conveners</div>
                    </td>
                    <td>
                      <span 
                        class="status-badge"
                        :class="getStatusClass(session.status)"
                      >
                        {{ getStatusText(session.status) }}
                      </span>
                    </td>
                    <td>
                      <div class="action-buttons">
                        <button 
                          class="btn btn-sm btn-outline-primary"
                          @click="viewSessionDetails(session.id)"
                        >
                          <i class="fa fa-eye"></i> View
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- 分页 -->
            <div class="pagination-section">
              <div class="pagination-container">
                <!-- 分页信息 -->
                <div class="pagination-info">
                  <span class="info-text">
                    Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, filteredSessions.length) }} of {{ filteredSessions.length }} entries
                  </span>
                </div>
                
                <!-- 分页控件 -->
                <div class="pagination-controls">
                  <!-- 每页显示数量选择 -->
                  <div class="page-size-selector">
                    <label>Show:</label>
                    <select v-model="pageSize" @change="changePageSize" class="form-control form-control-sm">
                      <option value="10">10</option>
                      <option value="20">20</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </div>
                  
                  <!-- 分页按钮 -->
                  <nav class="pagination-nav">
                    <ul class="pagination-list">
                      <!-- 首页 -->
                      <li class="pagination-item" :class="{ disabled: currentPage === 1 }">
                        <button 
                          class="pagination-btn" 
                          @click="changePage(1)"
                          :disabled="currentPage === 1"
                          title="First"
                        >
                          <i class="fa fa-angle-double-left"></i>
                        </button>
                      </li>
                      
                      <!-- 上一页 -->
                      <li class="pagination-item" :class="{ disabled: currentPage === 1 }">
                        <button 
                          class="pagination-btn" 
                          @click="changePage(currentPage - 1)"
                          :disabled="currentPage === 1"
                          title="Previous"
                        >
                          <i class="fa fa-angle-left"></i>
                        </button>
                      </li>
                      
                      <!-- 页码 -->
                      <li 
                        v-for="page in visiblePages" 
                        :key="page"
                        class="pagination-item"
                        :class="{ active: page === currentPage }"
                      >
                        <button 
                          class="pagination-btn" 
                          @click="changePage(page)"
                        >
                          {{ page }}
                        </button>
                      </li>
                      
                      <!-- 下一页 -->
                      <li class="pagination-item" :class="{ disabled: currentPage === totalPages }">
                        <button 
                          class="pagination-btn" 
                          @click="changePage(currentPage + 1)"
                          :disabled="currentPage === totalPages"
                          title="Next"
                        >
                          <i class="fa fa-angle-right"></i>
                        </button>
                      </li>
                      
                      <!-- 末页 -->
                      <li class="pagination-item" :class="{ disabled: currentPage === totalPages }">
                        <button 
                          class="pagination-btn" 
                          @click="changePage(totalPages)"
                          :disabled="currentPage === totalPages"
                          title="Last"
                        >
                          <i class="fa fa-angle-double-right"></i>
                        </button>
                      </li>
                    </ul>
                  </nav>
                  
                  <!-- 跳转输入框 -->
                  <div class="page-jump">
                    <label>Go to:</label>
                    <input 
                      type="number" 
                      v-model.number="jumpPage" 
                      @keyup.enter="jumpToPage"
                      class="form-control form-control-sm jump-input"
                      :min="1" 
                      :max="totalPages"
                      placeholder="Page"
                    />
                    <button 
                      class="btn btn-sm btn-outline-primary jump-btn"
                      @click="jumpToPage"
                    >
                      Go
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from '@/components/Layout.vue'
import { apiService } from '@/config/api'

export default {
  name: 'SessionList',
  components: {
    Layout
  },
  data() {
    return {
      searchKeyword: '',
      selectedPrimarySection: '',
      selectedStatus: '',
      sortBy: 'codeA',
      currentPage: 1,
      pageSize: 20,
      jumpPage: 1,
      sessions: [
        {
          id: 1426,
          session_no: 'AS01',
          title: 'Climate Change and Tropical Climatic Hazards in Asia Oceania',
          primarySection: 'AS',
          secondarySection: 'IG',
          status: 'Withdrawn',
          conveners: [
            { name: 'Prof Yuriy Kuleshov', affiliation: 'Bureau of Meteorology, Australia' },
            { name: 'Prof Johnny Chan', affiliation: 'Asia-Pacific Typhoon Collaborative Research Center, China' }
          ]
        },
        {
          id: 1427,
          session_no: 'AS01',
          title: 'Climate Change and Tropical Climatic Hazards in Asia Oceania',
          primarySection: 'AS',
          secondarySection: 'IG',
          status: 'Withdrawn',
          conveners: [
            { name: 'Prof Yuriy Kuleshov', affiliation: 'Bureau of Meteorology, Australia' },
            { name: 'Prof Johnny Chan', affiliation: 'Asia-Pacific Typhoon Collaborative Research Center, China' }
          ]
        },
        {
          id: 1428,
          session_no: 'AS01',
          title: 'Climate Change and Tropical Climatic Hazards in Asia Oceania',
          primarySection: 'AS',
          secondarySection: 'IG',
          status: 'Withdrawn',
          conveners: [
            { name: 'Prof Yuriy Kuleshov', affiliation: 'Bureau of Meteorology, Australia' },
            { name: 'Prof Johnny Chan', affiliation: 'Asia-Pacific Typhoon Collaborative Research Center, China' }
          ]
        },
        {
          id: 1429,
          session_no: 'AS01',
          title: 'Climate Change and Tropical Climatic Hazards in Asia Oceania',
          primarySection: 'AS',
          secondarySection: 'IG',
          status: 'Withdrawn',
          conveners: [
            { name: 'Prof Yuriy Kuleshov', affiliation: 'Bureau of Meteorology, Australia' },
            { name: 'Prof Johnny Chan', affiliation: 'Asia-Pacific Typhoon Collaborative Research Center, China' }
          ]
        },

        {
          id: 1418,
          session_no: 'AS03',
          title: 'Biomass Burning Emission and Impacts on the Environment in Southeast Asia',
          primarySection: 'AS',
          secondarySection: '',
          status: 'Active',
          conveners: [
            { name: 'Dr Shantanu Kumar Pani', affiliation: 'National Central University, Taiwan' },
            { name: 'Dr Maggie Chel Gee Ooi', affiliation: 'Universiti Kebangsaan Malaysia, Malaysia' }
          ]
        },
        {
          id: 1414,
          session_no: 'AS04',
          title: 'Integrating Ground Networks, Satellites, and Modeling for Enhanced Atmospheric Aerosols Insights',
          primarySection: 'AS',
          secondarySection: 'IG',
          status: 'Active',
          conveners: [
            { name: 'Dr Pawan Gupta', affiliation: 'NASA Goddard Space Flight Center, United States' },
            { name: 'Prof Kazuma Aoki', affiliation: 'University of Toyama, Japan' },
            { name: 'Prof Jhoon Kim', affiliation: 'Yonsei University, Korea, South' },
            { name: 'Prof Sheng-Hsiang Wang', affiliation: 'National Central University, Taiwan' },
            { name: 'Dr Ellsworth Welton', affiliation: 'National Aeronautics and Space Administration, United States' }
          ]
        },
        {
          id: 1419,
          session_no: 'AS06',
          title: 'Application of Satellite Data to Weather, Climate and Environmental Study',
          primarySection: 'AS',
          secondarySection: 'HS',
          status: 'Active',
          conveners: [
            { name: 'Prof Chian-Yi Liu', affiliation: 'Academia Sinica, Taiwan' },
            { name: 'Prof Myoung Hwan Ahn', affiliation: 'Ewha Womans University, Korea, South' },
            { name: 'Prof Jun Li', affiliation: 'National Satellite Meteorological Center, China' },
            { name: 'Prof Min Min', affiliation: 'Sun Yat-sen University, China' },
            { name: 'Dr Kozo Okamoto', affiliation: 'Japan Meteorological Agency, Japan' }
          ]
        }
      ]
    }
  },
  computed: {
    // 确保sessions是数组
    validSessions() {
      return Array.isArray(this.sessions) ? this.sessions : []
    },
    
    filteredSessions() {
      let filtered = this.validSessions

      // 搜索过滤
      if (this.searchKeyword) {
        const keyword = this.searchKeyword.toLowerCase()
        filtered = filtered.filter(session => 
          (session.session_no && session.session_no.toLowerCase().includes(keyword)) ||
          (session.title && session.title.toLowerCase().includes(keyword)) ||
          (session.conveners && session.conveners.toLowerCase().includes(keyword))
        )
      }

      // 主要部分过滤
      if (this.selectedPrimarySection) {
        filtered = filtered.filter(session => 
          session.primary_section === this.selectedPrimarySection
        )
      }

      // 状态过滤
      if (this.selectedStatus) {
        filtered = filtered.filter(session => 
          this.getStatusText(session.status) === this.selectedStatus
        )
      }

      return filtered
    },
    sortedSessions() {
      const sessions = [...this.filteredSessions]
      
      switch (this.sortBy) {
        case 'codeA':
          return sessions.sort((a, b) => (a.session_no || '').localeCompare(b.session_no || ''))
        case 'codeD':
          return sessions.sort((a, b) => (b.session_no || '').localeCompare(a.session_no || ''))
        case 'titleA':
          return sessions.sort((a, b) => (a.title || '').localeCompare(b.title || ''))
        case 'titleD':
          return sessions.sort((a, b) => (b.title || '').localeCompare(a.title || ''))
        case 'statA':
          return sessions.sort((a, b) => this.getStatusText(a.status || '').localeCompare(this.getStatusText(b.status || '')))
        case 'statD':
          return sessions.sort((a, b) => this.getStatusText(b.status || '').localeCompare(this.getStatusText(a.status || '')))
        default:
          return sessions
      }
    },
    paginatedSessions() {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.sortedSessions.slice(start, end)
    },
    totalPages() {
      return Math.ceil(this.filteredSessions.length / this.pageSize)
    },
    visiblePages() {
      const pages = []
      const maxVisible = 5
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2))
      let end = Math.min(this.totalPages, start + maxVisible - 1)
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1)
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      
      return pages
    }
  },
  created() {
    this.getSessions()
  },
  methods: {
    async getSessions() {
      try {
        const response = await apiService.sessions.getSessionList({ page: 1, limit: 100 })
        
        if (response.data && response.data.code === 1) {
          const apiData = response.data.data
          if (apiData && apiData.list && Array.isArray(apiData.list)) {
            this.sessions = apiData.list
          } else if (Array.isArray(apiData)) {
            this.sessions = apiData
          } else {
            console.error('API返回的数据格式不正确:', apiData)
            // 保持默认的模拟数据
          }
        } else {
          console.error('获取会议列表失败:', response.data)
          // 保持默认的模拟数据
        }
      } catch (error) {
        console.error('获取会议列表出错:', error)
        // 保持默认的模拟数据
      }
    },
    filterSessions() {
      this.currentPage = 1
    },
    sortSessions() {
      this.currentPage = 1
    },
    sortByField(field) {
      if (field === 'code') {
        this.sortBy = this.sortBy === 'codeA' ? 'codeD' : 'codeA'
      } else if (field === 'status') {
        this.sortBy = this.sortBy === 'statA' ? 'statD' : 'statA'
      }
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page
      }
    },
    getStatusClass(status) {
      switch (status) {
        case 1: return 'status-active'
        case 2: return 'status-withdrawn'
        case 0: return 'status-pending'
        case -1: return 'status-default'
        default: return 'status-default'
      }
    },
    getStatusText(status) {
      switch (status) {
        case 1: return 'Active'
        case 2: return 'Withdrawn'
        case 0: return 'Pending'
        case -1: return 'Inactive'
        default: return 'Unknown'
      }
    },
    viewSessionDetails(sessionId) {
      this.$router.push(`/session-details/${sessionId}`)
    },
    changePageSize() {
      this.currentPage = 1
    },
    jumpToPage() {
      if (this.jumpPage >= 1 && this.jumpPage <= this.totalPages) {
        this.currentPage = this.jumpPage
      }
    }
  }
}
</script>

<style scoped>
.session-list-container {
  padding: 20px 0;
  background-color: #f8f9fa;
  min-height: 100vh;
  overflow-x: hidden;
  width: 100%;
}

.page-title {
  color: #333;
  margin-bottom: 30px;
  font-weight: 600;
}

.search-filter-section {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.results-info {
  font-size: 14px;
  color: #666;
}

.table {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.thead-dark th {
  background-color: #0055a7;
  border-color: #454d55;
  color: white;
}

.thead-dark th a {
  text-decoration: none;
  color: white;
}

.thead-dark th a:hover {
  color: #fff;
  text-decoration: none;
}

.session-row:hover {
  background-color: #f8f9fa;
}

.session-info {
  display: flex;
  flex-direction: column;
}

.session-code {
  font-weight: bold;
  color: #3498DB;
  text-decoration: none;
  font-size: 16px;
}

.session-code:hover {
  color: #0056b3;
  text-decoration: underline;
}

.session-title {
  font-size: 14px;
  color: #666;
  margin-top: 4px;
  line-height: 1.4;
}

.session-meta {
  margin-top: 4px;
  font-size: 12px;
}

.section-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  text-align: center;
  min-width: 30px;
}

.primary-section {
  background-color: #3498DB;
  color: white;
}

.secondary-section {
  background-color: #6c757d;
  color: white;
}

.conveners-list {
  font-size: 13px;
  line-height: 1.4;
}

.convener-item {
  margin-bottom: 4px;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
}

.status-active {
  background-color: #28a745;
  color: white;
}

.status-withdrawn {
  background-color: #a70001;
  color: white;
}

.status-pending {
  background-color: #ffc107;
  color: #212529;
}

.status-default {
  background-color: #6c757d;
  color: white;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.pagination-section {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-top: 20px;
}

.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.pagination-info {
  color: #666;
  font-size: 14px;
}

.pagination-controls {
  display: flex;
  align-items: center;
}

.page-size-selector {
  margin-right: 20px;
}

.page-size-selector label {
  margin-right: 5px;
}

.pagination-nav {
  margin: 0 20px;
}

.pagination-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.pagination-item {
  display: inline;
}

.pagination-btn {
  background: #fff;
  border: 1px solid #ddd;
  padding: 6px 12px;
  cursor: pointer;
  color: #333;
  text-decoration: none;
  border-radius: 3px;
  margin: 0 2px;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #3498DB;
  color: #3498DB;
}

.pagination-btn:disabled {
  color: #999;
  cursor: not-allowed;
  opacity: 0.6;
}

.pagination-item.active .pagination-btn {
  background: #3498DB;
  border-color: #3498DB;
  color: #fff;
}

.page-jump {
  margin-left: 20px;
  display: flex;
  align-items: center;
}

.page-jump label {
  margin-right: 5px;
  font-size: 14px;
}

.jump-input {
  width: 50px;
  margin: 0 5px;
  text-align: center;
}

.jump-btn {
  padding: 4px 8px;
  font-size: 12px;
}

.page-size-selector {
  margin-right: 20px;
  display: flex;
  align-items: center;
}

.page-size-selector label {
  margin-right: 5px;
  font-size: 14px;
}

.page-size-selector select {
  width: 60px;
}

.info-text {
  font-size: 14px;
  color: #666;
}

@media (max-width: 768px) {
  .search-filter-section {
    padding: 15px;
  }
  
  .search-filter-section .row > div {
    margin-bottom: 15px;
  }
  
  .table-responsive {
    font-size: 14px;
    overflow-x: auto;
  }
  
  .table {
    min-width: 100%;
  }
  
  .session-title {
    font-size: 13px;
  }
  
  .conveners-list {
    font-size: 12px;
  }
  
  /* 分页移动端优化 */
  .pagination-container {
    flex-direction: column;
    gap: 15px;
  }
  
  .pagination-controls {
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
    width: 100%;
  }
  
  .page-size-selector,
  .page-jump {
    margin: 0;
  }
  
  .pagination-nav {
    margin: 0 10px;
    order: 1;
  }
  
  .pagination-list {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 5px;
  }
  
  .pagination-btn {
    padding: 8px 10px;
    font-size: 14px;
    min-width: 35px;
  }
  
  .page-size-selector {
    order: 2;
  }
  
  .page-jump {
    order: 3;
  }
  
  .jump-input {
    width: 60px;
  }
  
  .info-text {
    text-align: center;
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .pagination-controls {
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }
  
  .pagination-nav {
    margin: 0;
  }
  
  .pagination-list {
    gap: 3px;
  }
  
  .pagination-btn {
    padding: 6px 8px;
    font-size: 13px;
    min-width: 30px;
  }
  
  .page-size-selector,
  .page-jump {
    display: flex;
    align-items: center;
    gap: 5px;
  }
  
  .jump-input {
    width: 50px;
  }
}
</style> 